"use client"
import LandingPageHeader from '../Header/LandingPageHeader';
import LogoAndNameImage from '../Logo/LogoAndName';
import styles from './LandingPage.module.css';
import { SearchComponent } from '../SearchComponent/SearchComponent';
import { useEffect, useState, useRef, useMemo } from 'react';
import QuerySuggestion from './QuerySuggestions';
import PromptSelector from '@/app/discovery-help/PromptSelector';
import { landingPageBooks } from './landingPageBooks';
import Footer from './LandingPageFooter';
import { track } from '@vercel/analytics/react';
import AnimatedArrow from '../ui/AnimatedArrow';
const LandingPage: React.FC = () => {
  // Use the current minute to determine the initial book index
  const getInitialBookIndex = () => {
    const now = new Date();
    const currentMinute = now.getMinutes();
    return currentMinute % landingPageBooks.length;
  };

  const [currentBookIndex] = useState(getInitialBookIndex);
  const [formattedDate, setFormattedDate] = useState('');
  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');
  const [seconds, setSeconds] = useState('');
  const [searchQuery, setSearchQuery] = useState("")
  const [hasGenerated, setHasGenerated] = useState(false)
  const [generatedSuggestions, setGeneratedSuggestions] = useState<string[]>([])
  const inputRef = useRef<HTMLInputElement>(null)

  // Memoize the current book to prevent unnecessary re-renders
  const currentBook = useMemo(() => landingPageBooks[currentBookIndex], [currentBookIndex]);

  // Clock update effect
  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      setFormattedDate(now.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }));
      setHours(now.getHours().toString().padStart(2, '0'));
      setMinutes(now.getMinutes().toString().padStart(2, '0'));
      setSeconds(now.getSeconds().toString().padStart(2, '0'));
    };

    updateClock();
    const clockInterval = setInterval(updateClock, 1000);

    return () => clearInterval(clockInterval);
  }, []);

  const handleGenerate = () => {
    if (searchQuery.trim() !== "") {
      setHasGenerated(true)
    } else if (inputRef.current) {
      inputRef.current.focus()
    }
  }
  
  // Handlers for the PromptSelector component
  const handleGenerateStatus = (status: boolean) => {
    setHasGenerated(status);
  }
  
const handleSuggestionsUpdate = (suggestions: string[]) => {
  setGeneratedSuggestions(suggestions);
  
  // Pre-fetch results for each suggestion on the client side
  suggestions.forEach(async (suggestion) => {
    const queryText = suggestion.trim().toLowerCase();

    
    try {

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/api/multimodal-search?query=${encodeURIComponent(queryText)}&skipEnhancement=true`
      );
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      const results = await response.json();
      
    
      
    } catch (error) {
      console.error(`Failed to pre-fetch results for: ${queryText}`, error);
    } 
  });
};

  return (
    <div className={styles.overallContainer}>
      <LandingPageHeader />
      <div className={styles.timestamp}>
              <span>{formattedDate}</span>
              <div className={styles.timeContainer}>
                <span className={styles.timeDigit}>{hours}</span>
                <span className={styles.timeSeparator}>:</span>
                <span className={styles.timeDigit}>{minutes}</span>
                <span className={styles.timeSeparator}>:</span>
                <span className={styles.timeDigit}>{seconds}</span>
              </div>
            </div>
      <div className={styles.viewPortOne}>
        {/* <LandingPageHeader /> */}
        <div className={styles.layoutWrapper}>
          <main className={styles.mainContent}>
            <div className={styles.logoContainer}>
              <LogoAndNameImage className={styles.landingPageLogo} />
            </div>
            <h1 className={styles.mainTitle}>
              Discover books by mood and premise
            </h1>
            <div className={styles.searchContainer}>
              <SearchComponent 
                containerClassName={styles.landingPageSearch}
                formClassName={styles.searchForm}
                inputWrapperClassName={styles.inputWrapper}
                buttonClassName={styles.searchButton}
                inputClassName={styles.searchInput}
              />
            </div>
            <AnimatedArrow />
          </main>
          <div 
            className={styles.bookImageContainer}
            style={{ backgroundColor: currentBook.backgroundColor }}
          >

            <div className={styles.bookInfo}>
              <h2 className={styles.bookTitle}>{currentBook.title}</h2>
              <p className={styles.bookAuthor}>{currentBook.author}</p>
            </div>
            <div className={styles.bookCover}>
              <div >
                <img
                  src={currentBook.imageUrl} 
                  alt={`Cover of ${currentBook.title}`} 
                  className={styles.coverImage}
                />
              </div>
            </div>
          </div>
        </div>

      </div>
      <div className={styles.container}>
        <main className={styles.main}>
          <div className={`${styles.titleContainer} ${hasGenerated ? styles.titleTop : styles.titleCenter}`}>
            <h1 className={styles.title}>Embark on your next book adventure.</h1>
            <p className={`${styles.subtitle} ${hasGenerated ?  styles.suggestionsHidden: styles.suggestionsVisible}`}>
              {/* Generating prompts for book recommendations involves asking targeted questions about genre, themes, tone, and preferred authors. */}
              We can generate a few search suggestions for you based on other books, moods or anything you like. Don't like the first options? Just generate again!
              {!hasGenerated && (<><br /></>)}
            </p>
          </div>

          <div className={`${styles.promptContainer} ${hasGenerated ? styles.promptBottom : styles.promptCenter}`}>
            <PromptSelector 
              onGenerateStatus={handleGenerateStatus}
              onSuggestionsUpdate={handleSuggestionsUpdate}
              externalHasGenerated={hasGenerated}
            /> 
          </div>

          <div
            className={`${styles.suggestionsContainer} ${hasGenerated ? styles.suggestionsVisible : styles.suggestionsHidden}`}
            style={{ transitionDelay: hasGenerated ? "400ms" : "0ms" }}
          >
            <div className={styles.suggestionsWrapper}>
              <div className={styles.suggestionsGrid}>
                {generatedSuggestions.map((suggestion, index) => (
                  <QuerySuggestion
                    key={index}
                    title={`Suggestion`}
                    description={suggestion}
                    onClick={() => {
                      sessionStorage.setItem('lastExactQuery', suggestion.trim());
                      track("clicked suggestion", {
                        suggestion: index
                      })
                      const slugifiedQuery = suggestion.trim()
                        .toLowerCase()
                        .replace(/[\/\?\#\:\*\[\]\@\!\$\&\'\(\)\+\,\;\=]/g, '-')
                        .replace(/\s+/g, '-')
                        .replace(/-+/g, '-');
                      window.location.href = `/discover/${slugifiedQuery}?from=suggestions`;
                    }}
                    delay={index * 100 + 600}
                    isVisible={hasGenerated}
                    backgroundColor={currentBook.backgroundColor}
                  />
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>
      <Footer backgroundColor={currentBook.backgroundColor} />
    </div>
  );
};

export default LandingPage 